import React from 'react'
import { Link } from 'react-router-dom'
import '../App.css'

function GymXpPrivacyPolicy() {
  return (
    <div className='privacy-policy-container'>
      <div className='privacy-policy-nav'>
        <Link to='/' className='privacy-back-link'>← Back to Home</Link>
      </div>
      <div className='privacy-policy-content'>
        <h1>Privacy Policy</h1>
        <p className='last-updated'>Last Updated: {new Date().toLocaleDateString()}</p>
        
        <section className='privacy-section'>
          <h2>1. Introduction</h2>
          <p>
            Welcome to GymXp ("we," "our," or "us"). This Privacy Policy explains how we collect, 
            use, disclose, and safeguard your information when you use our gym tracking and streak 
            maintenance application. Please read this privacy policy carefully. If you do not agree 
            with the terms of this privacy policy, please do not access the application.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Information You Provide to Us</h3>
          <ul>
            <li><strong>Photos and Images:</strong> When you capture and post photos of gym equipment 
            using the live camera feature, we collect and store these images on our servers. These 
            photos are used to verify your gym attendance and maintain your daily streak.</li>
            <li><strong>Account Information:</strong> Name, email address, username, profile picture, 
            and any other information you choose to provide during account creation.</li>
            <li><strong>Streak Data:</strong> Information about your gym attendance, daily streaks, 
            and streak history.</li>
            <li><strong>Freeze Usage:</strong> Information about your use of the freeze feature, 
            including when and how you earned freezes through rewarded ads.</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li><strong>Camera Access:</strong> We access your device's camera to allow you to capture 
            photos of gym equipment. We only access the camera when you actively use this feature.</li>
            <li><strong>Usage Data:</strong> How you interact with the application, features you use, 
            time spent on the platform, and frequency of gym check-ins.</li>
            <li><strong>Device Information:</strong> Device type, operating system, unique device 
            identifiers, mobile network information, and camera specifications.</li>
            <li><strong>Log Data:</strong> IP address, browser type, access times, pages viewed, and 
            app performance metrics.</li>
            <li><strong>Location Data:</strong> If you enable location services, we may collect 
            information about your location to enhance gym verification features (optional).</li>
            <li><strong>Ad Interaction Data:</strong> Information about your interactions with Google 
            rewarded ads, including ad views, completions, and rewards earned.</li>
          </ul>
        </section>

        <section className='privacy-section'>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for various purposes, including:</p>
          <ul>
            <li><strong>Service Provision:</strong> To provide, maintain, and improve our services, 
            including gym attendance verification, streak tracking, and freeze feature management.</li>
            <li><strong>AI Model Processing:</strong> Your captured photos of gym equipment are 
            processed using our AI model to:
              <ul>
                <li>Detect and verify gym equipment in your photos</li>
                <li>Validate your gym attendance for daily streak purposes</li>
                <li>Improve the accuracy of equipment detection</li>
                <li>Prevent fraudulent streak claims</li>
              </ul>
            </li>
            <li><strong>Model Training and AI Development:</strong> Your uploaded photos of gym 
            equipment may be used to train, develop, and improve machine learning models, artificial 
            intelligence systems, and computer vision technologies. This includes but is not limited to:
              <ul>
                <li>Gym equipment recognition and classification</li>
                <li>Image processing and enhancement</li>
                <li>Fraud detection and prevention</li>
                <li>Improving detection accuracy and reliability</li>
              </ul>
            </li>
            <li><strong>Streak Management:</strong> To track and maintain your daily gym streak, 
            verify attendance, and manage freeze usage.</li>
            <li><strong>Ad Services:</strong> To display Google rewarded ads and track your ad 
            interactions for the freeze feature reward system.</li>
            <li><strong>Personalization:</strong> To personalize your experience, provide gym 
            tracking insights, and show you relevant content and statistics.</li>
            <li><strong>Communication:</strong> To send you updates, notifications about your streak, 
            reminders, and respond to your inquiries.</li>
            <li><strong>Analytics:</strong> To analyze usage patterns, improve our services, conduct 
            research, and understand user behavior.</li>
            <li><strong>Safety and Security:</strong> To detect, prevent, and address technical 
            issues, fraud, abuse, or misuse of the streak system.</li>
          </ul>
        </section>

        <section className='privacy-section'>
          <h2>4. How We Share Your Information</h2>
          
          <h3>4.1 Third-Party Service Providers</h3>
          <p>
            We may share your information, including photos and images, with third-party service 
            providers who perform services on our behalf, such as:
          </p>
          <ul>
            <li>Cloud storage providers for photo storage</li>
            <li>AI and machine learning service providers for equipment detection</li>
            <li>Google AdMob for rewarded ad services</li>
            <li>Analytics services</li>
            <li>Content delivery networks</li>
            <li>Customer support services</li>
          </ul>

          <h3>4.2 Google Ad Services</h3>
          <p>
            We use Google AdMob to display rewarded ads in our application. When you interact with 
            these ads, Google may collect certain information according to their privacy policy. 
            This information is used to:
          </p>
          <ul>
            <li>Display relevant rewarded ads</li>
            <li>Track ad completions for freeze rewards</li>
            <li>Prevent ad fraud</li>
          </ul>
          <p>
            For more information about how Google uses your data, please review Google's Privacy 
            Policy at <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a>.
          </p>

          <h3>4.3 Third-Party Partners for Model Training</h3>
          <p>
            Your uploaded photos of gym equipment may be shared with third-party partners, including 
            research institutions, technology companies, and AI development organizations, for the 
            purpose of:
          </p>
          <ul>
            <li>Training and developing machine learning models for equipment detection</li>
            <li>Improving AI and computer vision technologies</li>
            <li>Conducting fitness and gym equipment research</li>
            <li>Developing new features and capabilities</li>
          </ul>
          <p>
            <strong>Important:</strong> By using GymXp, you consent to the use of your photos and 
            images for these purposes, including sharing with third parties for model training and 
            AI development.
          </p>

          <h3>4.4 Legal Requirements</h3>
          <p>
            We may disclose your information if required by law, court order, or government regulation, 
            or if we believe disclosure is necessary to protect our rights, property, or safety, or 
            that of our users or others.
          </p>

          <h3>4.5 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, or sale of assets, your information may be transferred 
            to the acquiring entity.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>5. Camera Access and Photo Capture</h2>
          <p>
            GymXp requires access to your device's camera to enable the photo capture feature. 
            Important information about camera usage:
          </p>
          <ul>
            <li><strong>Permission:</strong> We will request camera permission when you first use 
            the photo capture feature. You can revoke this permission at any time through your 
            device settings, though this will limit your ability to use the core features of the app.</li>
            <li><strong>Live Camera:</strong> The app uses your device's live camera feed only when 
            you actively open the camera interface. We do not access your camera in the background.</li>
            <li><strong>Photo Storage:</strong> Photos you capture are stored on our servers for 
            verification purposes and may be used for AI model training as described in this policy.</li>
            <li><strong>Photo Deletion:</strong> You can request deletion of your photos, though 
            please note that photos used for model training may have been incorporated into trained 
            models and complete removal may not be possible.</li>
          </ul>
        </section>

        <section className='privacy-section'>
          <h2>6. AI Model and Equipment Detection</h2>
          <p>
            Our application uses artificial intelligence to detect gym equipment in your photos. 
            This process involves:
          </p>
          <ul>
            <li><strong>Image Analysis:</strong> Your photos are analyzed by our AI model to identify 
            gym equipment and verify authenticity.</li>
            <li><strong>Streak Verification:</strong> Successful detection of gym equipment in your 
            photo confirms your gym attendance for that day, allowing you to maintain your streak.</li>
            <li><strong>Model Improvement:</strong> Your photos may be used to improve the accuracy 
            and performance of our detection model.</li>
            <li><strong>Fraud Prevention:</strong> The AI model helps prevent fraudulent streak claims 
            by verifying that photos contain actual gym equipment.</li>
          </ul>
        </section>

        <section className='privacy-section'>
          <h2>7. Rewarded Ads and Freeze Feature</h2>
          <p>
            GymXp uses Google rewarded ads to allow you to earn freezes for your streak. When you 
            watch rewarded ads:
          </p>
          <ul>
            <li><strong>Ad Interaction:</strong> We track when you complete rewarded ads to award 
            you freezes.</li>
            <li><strong>Google Data Collection:</strong> Google may collect information about your 
            ad interactions according to their privacy policy.</li>
            <li><strong>Freeze Usage:</strong> We track your freeze usage to manage your streak 
            protection feature.</li>
            <li><strong>Ad Personalization:</strong> Ads may be personalized based on your device 
            information and usage patterns.</li>
          </ul>
        </section>

        <section className='privacy-section'>
          <h2>8. Data Storage and Security</h2>
          <p>
            Your photos, streak data, and information are stored on our secure servers and may be 
            stored in databases located in various jurisdictions. We implement appropriate technical 
            and organizational measures to protect your information against unauthorized access, 
            alteration, disclosure, or destruction. However, no method of transmission over the 
            Internet or electronic storage is 100% secure.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>9. Your Rights and Choices</h2>
          <p>You have certain rights regarding your information:</p>
          <ul>
            <li><strong>Access:</strong> You can access and update your account information through 
            the application settings.</li>
            <li><strong>Camera Permission:</strong> You can grant or revoke camera permission through 
            your device settings at any time.</li>
            <li><strong>Photo Deletion:</strong> You can request deletion of your photos. However, 
            please note that:
              <ul>
                <li>Some information may be retained for legal or legitimate business purposes</li>
                <li>Photos that have been shared with third parties for model training may continue 
                to be used in accordance with this policy</li>
                <li>Photos incorporated into trained AI models may not be fully removable</li>
              </ul>
            </li>
            <li><strong>Account Deletion:</strong> You can delete your account and request deletion 
            of your data. This will remove your streak history and account information.</li>
            <li><strong>Opt-Out:</strong> You can opt out of certain data uses, though this may limit 
            your ability to use some features, including the freeze feature through rewarded ads.</li>
            <li><strong>Data Portability:</strong> You may request a copy of your data in a 
            machine-readable format.</li>
          </ul>
          <p>
            <strong>Note:</strong> Due to the nature of machine learning and AI development, photos 
            used for model training may have been incorporated into trained models, and complete 
            removal from all systems may not be possible.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>10. Children's Privacy</h2>
          <p>
            GymXp is not intended for children under the age of 13. We do not knowingly collect 
            personal information from children under 13. If you are a parent or guardian and believe 
            your child has provided us with personal information, please contact us immediately.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>11. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country 
            of residence. These countries may have data protection laws that differ from those in 
            your country. By using GymXp, you consent to the transfer of your information to these 
            countries.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>12. Data Retention</h2>
          <p>
            We retain your information for as long as necessary to provide our services, comply with 
            legal obligations, resolve disputes, and enforce our agreements. Photos and images used 
            for model training may be retained for extended periods as they may be incorporated into 
            trained models and datasets. Streak data is retained as long as your account is active 
            and may be retained for a reasonable period after account deletion for legal and business 
            purposes.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes 
            by posting the new Privacy Policy on this page and updating the "Last Updated" date. 
            You are advised to review this Privacy Policy periodically for any changes.
          </p>
        </section>

        <section className='privacy-section'>
          <h2>14. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <ul>
            <li>Email: baralnishan987@gmail.com</li>
            <li>Through the contact form on our website</li>
          </ul>
        </section>

        <section className='privacy-section'>
          <h2>15. Your Consent</h2>
          <p>
            By using GymXp, you consent to this Privacy Policy and agree to its terms. If you do not 
            agree with this policy, please do not use our services.
          </p>
        </section>
      </div>
    </div>
  )
}

export default GymXpPrivacyPolicy






